/** BUY NOW keeps exactly one SOOID package and opens Shopify checkout. */
(() => {
  let purchasing = false;

  function showError(form, message) {
    const status = form.querySelector('[data-purchase-status]');
    if (!status) return;
    status.textContent = message;
  }

  async function request(url, body) {
    const endpoint = new URL(url, window.location.origin);
    if (!endpoint.pathname.endsWith('.js')) endpoint.pathname += '.js';
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(endpoint.pathname + endpoint.search, {
        method: body ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
        cache: 'no-store',
        signal: controller.signal,
      });
      let data = null;
      try { data = await response.json(); } catch { data = null; }
      if (!response.ok || !data || data.status) {
        const failure = new Error('We couldn’t start checkout. Please try again.');
        if (data && typeof data.description === 'string' && data.description) {
          failure.message = data.description;
          failure.fromShopify = true;
        }
        throw failure;
      }
      return data;
    } finally {
      clearTimeout(timeout);
    }
  }

  // PDP BUY NOW links in the header and content use the currently selected pack.
  // Links on pages without a purchase form keep their ordinary destination.
  document.addEventListener('click', event => {
    const action = event.target.closest('a, button');
    if (!action || action.matches('[data-atc], [data-sticky-add]') || action.closest('[data-purchase-form]')) return;
    if (action.textContent.trim().replace(/\s+/g, ' ').toUpperCase() !== 'BUY NOW') return;
    const form = document.querySelector('[data-pdp-form]');
    const submit = form?.querySelector('[data-atc]');
    if (!form || !submit || submit.disabled) return;
    event.preventDefault();
    form.requestSubmit ? form.requestSubmit(submit) : submit.click();
  });

  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-purchase-form]') || event.defaultPrevented) return;
    const purchase = form.closest('sooid-purchase');
    // The native form remains the fallback only before a cart mutation starts.
    if (!purchase?.synchronize) return;
    event.preventDefault();
    const state = purchase.synchronize(form);
    if (purchasing || !state.valid) return;
    const item = { id: Number(state.variantId), quantity: Number(state.quantity) };
    if (!Number.isSafeInteger(item.id) || item.id <= 0 || !Number.isInteger(item.quantity) || item.quantity <= 0) return;
    if (state.planId) {
      item.selling_plan = Number(state.planId);
      if (!Number.isSafeInteger(item.selling_plan) || item.selling_plan <= 0) return;
    }
    purchasing = true;

    // Lock every purchase control so one gesture can create only one checkout mutation.
    const controls = [...document.querySelectorAll('sooid-purchase button, sooid-purchase select, sooid-purchase input, [data-sticky-add]')];
    const disabled = controls.map(control => control.disabled);
    controls.forEach(control => { control.disabled = true; });
    form.setAttribute('aria-busy', 'true');
    const atc = form.querySelector('[data-atc]');
    if (atc) atc.textContent = 'Opening checkout…';
    showError(form, '');

    let deferred = null;
    let errorMessage = '';
    try {
      const EventType = window.StandardEvents?.CartLinesUpdateEvent;
      if (EventType) {
        deferred = EventType.createPromise();
        purchase.dispatchEvent(new EventType({ action: 'add', context: 'product', lines: [{ merchandiseId: String(item.id), quantity: item.quantity }], promise: deferred.promise }));
      }
    } catch { deferred = null; /* Optional analytics must not block purchases. */ }

    try {
      let direct = null;
      try {
        direct = await window.SooidCheckout?.create(item, 'buy-now');
      } catch (failure) {
        if (failure?.retryable === false) throw failure;
      }
      if (direct) {
        try { deferred?.resolve({ cart: direct.cart }); } catch { /* analytics only */ }
        window.location.assign(direct.checkoutUrl);
        return;
      }

      // Technical Storefront API failures retain the slower Ajax cart path as a fallback.
      // Add first so an unavailable selection leaves the existing cart untouched.
      const added = await request(form.action, { items: [item] });
      const line = added.items?.find(entry => Number(entry.variant_id || entry.id) === item.id);
      if (!line?.key) throw new Error('We couldn’t start checkout. Please try again.');
      const wantedPlan = String(item.selling_plan || '');
      const gotPlan = String(line.selling_plan_allocation?.selling_plan?.id || '');
      if (wantedPlan !== gotPlan) {
        // Undo only the quantity just added if Shopify attached the wrong purchase type.
        await request(window.routes.cart_change_url, {
          id: line.key,
          quantity: Math.max(0, Number(line.quantity) - item.quantity),
        }).catch(() => {});
        throw new Error('We couldn’t start checkout. Please try again.');
      }

      const cart = await request(window.routes.cart_url);
      const updates = {};
      (cart.items || []).forEach(entry => {
        if (entry.key === line.key) {
          if (Number(entry.quantity) !== item.quantity) updates[entry.key] = item.quantity;
        } else {
          updates[entry.key] = 0;
        }
      });
      const final = Object.keys(updates).length
        ? await request(window.routes.cart_update_url, { updates, attributes: { _source: 'buy-now' } })
        : cart;
      const chosen = final.items?.[0];
      const chosenPlan = String(chosen?.selling_plan_allocation?.selling_plan?.id || '');
      if (final.items?.length !== 1 || chosen?.key !== line.key || Number(chosen?.variant_id || chosen?.id) !== item.id ||
          Number(chosen?.quantity) !== item.quantity || chosenPlan !== wantedPlan) {
        throw new Error('We couldn’t confirm the checkout contents. Please try again.');
      }

      try {
        if (deferred && final.currency) {
          deferred.resolve({ cart: window.StandardEvents.CartLinesUpdateEvent.createCartFromAjaxResponse(final) });
        }
      } catch { /* analytics only */ }
      window.location.assign('/checkout');
    } catch (failure) {
      deferred?.reject(failure);
      errorMessage = failure?.fromShopify ? failure.message : 'We couldn’t start checkout. Please try again.';
    } finally {
      purchasing = false;
      controls.forEach((control, index) => { control.disabled = disabled[index]; });
      form.removeAttribute('aria-busy');
      document.querySelectorAll('sooid-purchase').forEach(element => element.forms?.forEach(item => element.synchronize(item)));
      if (errorMessage) showError(form, errorMessage);
    }
  });
})();
